import { useActionDispatcher, useApi } from "@judo/actions";
import {
	DispatchProvider,
	PageProvider,
	RefreshSignalProvider,
	useApplication,
	useDataStore,
	useTransferDirty,
	useNavigation,
	usePageContext,
	useResolvedPageActionOverrides,
} from "@judo/core";
import { useNotifications } from "@judo/feedback";
import type { ClassType, Flex, PageContainer, PageDefinition, RelationType } from "@judo/model-api";
import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlexRenderer } from "../renderers/FlexRenderer";
import { PageHeader } from "../renderers/PageHeader";
import { buildViewMask } from "../utils/build-page-mask";
import { findOnInitAction } from "../utils/find-on-init-action";
import { getSingletonAccessRelation, isSingletonAccessPage } from "../utils/singleton-access";

/**
 * Resolve a ClassType from a dataElement, handling different types:
 * - ClassType: return directly
 * - RelationType: follow .target
 * - OperationParameterType (output/input): also follow .target (inherits from ReferenceType)
 *   These elements have no @type from the parser because inline <output>/<input> tags aren't mapped.
 */
function resolveClassTypeFromDataElement(dataElement: { "@type"?: string; target?: ClassType }): ClassType | undefined {
	if (dataElement["@type"] === "ClassType") {
		return dataElement as unknown as ClassType;
	}
	if (dataElement["@type"] === "RelationType") {
		return (dataElement as unknown as RelationType).target;
	}
	// OperationParameterType or other ReferenceType subtypes - follow .target if present
	if (dataElement.target) {
		return dataElement.target;
	}
	return undefined;
}

export interface ViewContainerRendererProps {
	page: PageDefinition;
}

/**
 * Renderer for VIEW type containers.
 * Edit mode is derived from transfer dirty state - when user modifies data, view enters edit mode.
 * Fetches transfer data on mount via onInit action (Refresh).
 */
export function ViewContainerRenderer({ page }: ViewContainerRendererProps) {
	const container = page.container as PageContainer;
	const pageContext = usePageContext();

	// Get required contexts for action dispatcher
	const navigation = useNavigation();
	const dataStore = useDataStore();
	const application = useApplication();
	const api = useApi();
	const notifications = useNotifications();

	// Resolve the page's ClassType (for representation attribute in mask)
	const pageClassType = useMemo(() => {
		const dataElement = page.dataElement || container.dataElement;
		return dataElement ? resolveClassTypeFromDataElement(dataElement) : undefined;
	}, [page.dataElement, container.dataElement]);

	// Compute the view mask from the container's visual tree
	const viewMask = useMemo(() => buildViewMask(container, pageClassType), [container, pageClassType]);

	// Find the onInit action
	const onInitAction = useMemo(() => {
		const action = findOnInitAction(page, container);

		// If action exists and has a RefreshActionDefinition
		if (action && action.actionDefinition["@type"] === "RefreshActionDefinition") {
			const existingTargetType = action.actionDefinition.targetType;

			// If no targetType is set, derive it from the dataElement
			if (!existingTargetType) {
				// Prefer page.dataElement, fallback to container.dataElement
				const dataElement = page.dataElement || container.dataElement;

				if (dataElement) {
					// Derive the ClassType from the dataElement:
					// - ClassType: use directly
					// - RelationType: follow .target to get ClassType
					// - OperationParameterType (output/input): also has .target via ReferenceType
					//   (no @type is set by the parser for inline <output>/<input> elements)
					const targetType = resolveClassTypeFromDataElement(dataElement);

					// Mutate the action definition to add targetType (parser should do this, but doesn't)
					if (targetType) {
						action.actionDefinition.targetType = targetType;
					} else {
						console.error("[ViewContainerRenderer] Failed to set valid targetType - could not derive from dataElement");
					}
				}
			}
		}

		return action;
	}, [page, container]);

	// Get transfer from page context
	// CRITICAL: Use pageContext.transferId as __identifier to ensure storage key matches
	// what useVisualBinding expects. PageRoute sets transferId = `transfer::${signedIdentifier}`
	// and useVisualBinding reads from pageContext.transferId.
	const entityFromContext = useMemo(() => {
		const transferId = pageContext?.transferId;
		const signedIdentifier = pageContext?.params?.signedIdentifier as string | undefined;

		// For dialog pages opened from table rows, signedIdentifier is not at the top level
		// of params. Instead, it's nested inside params.transfer.__signedIdentifier.
		const entityParam = pageContext?.params?.transfer as { __signedIdentifier?: string } | undefined;
		const effectiveSignedId = signedIdentifier ?? entityParam?.__signedIdentifier;

		if (!transferId || !effectiveSignedId) return undefined;

		const constructedEntity = {
			__identifier: transferId, // Full transfer ID: "transfer::abc123" or "dialog::transfer::abc123"
			__signedIdentifier: effectiveSignedId, // Bare signed identifier: "abc123"
		};

		return constructedEntity;
	}, [pageContext?.transferId, pageContext?.params]);

	// Singleton access page detection
	const isSingleton = useMemo(() => isSingletonAccessPage(page), [page]);

	const [singletonEntity, setSingletonEntity] = useState<
		{ __identifier: string; __signedIdentifier: string } | undefined
	>(undefined);
	const hasFetchedSingleton = useRef(false);

	useEffect(() => {
		if (!isSingleton || entityFromContext || hasFetchedSingleton.current) return;

		const relation = getSingletonAccessRelation(page);
		if (!relation || !api) return;

		hasFetchedSingleton.current = true;

		(async () => {
			try {
				const { data } = await api.refreshAccessRelation(relation, { _mask: "{}" });
				if (data?.__signedIdentifier) {
					const transferId = `transfer::${data.__signedIdentifier}`;
					setSingletonEntity({
						__identifier: transferId,
						__signedIdentifier: data.__signedIdentifier as string,
					});
				} else {
					console.error("[ViewContainerRenderer] Singleton access page returned no signedIdentifier");
				}
			} catch (error) {
				console.error("[ViewContainerRenderer] Error fetching singleton access transfer:", error);
			}
		})();
	}, [isSingleton, entityFromContext, page, api]);

	// Use transfer from context (normal flow) or singleton pre-fetch
	const transfer = entityFromContext ?? singletonEntity;

	// For singleton pages, override the transferId in page context so downstream
	// useVisualBinding can find data in the store under the correct key.
	const effectiveEntityId = transfer?.__identifier ?? pageContext?.transferId ?? null;

	// Derive edit mode from effective transfer ID (works for both regular and singleton pages)
	const isDirty = useTransferDirty(effectiveEntityId ?? "");
	const isEditMode = effectiveEntityId !== null && isDirty;

	// Extract eager context from page params (forwarded by RowOpenPageActionHandler)
	// When a dialog is opened from an eager table, these params tell the update handler
	// to skip REST calls and instead update the parent transfer's local relation data.
	const isEager = pageContext?.params?.isEager as boolean | undefined;
	const eagerParentEntityId = pageContext?.params?.parentTransferId as string | undefined;
	const eagerRelationName = pageContext?.params?.relationName as string | undefined;

	// Get resolved page action overrides from customizations
	const actionOverrides = useResolvedPageActionOverrides();

	// Page-wide refresh signal — operation handlers call refreshPage() to
	// trigger a full page refresh (transfer + embedded tables).
	const [refreshVersion, setRefreshVersion] = useState(0);
	const refreshPage = useCallback(() => setRefreshVersion((v) => v + 1), []);

	// Build effective params - for singleton pages, add the resolved signedIdentifier
	const effectiveParams = useMemo(() => {
		if (singletonEntity) {
			return {
				...pageContext?.params,
				signedIdentifier: singletonEntity.__signedIdentifier,
			};
		}
		return pageContext?.params ?? {};
	}, [singletonEntity, pageContext?.params]);

	// Memoize dispatcher context
	const dispatcherContext = useMemo(
		() =>
			({
				navigation,
				data: dataStore,
				registry: application,
				api,
				transfer,
				notifications,
				actionOverrides,
				additionalContext: {
					pageType: "VIEW",
					isEditMode,
					refreshPage,
					// Forward eager context so update handler knows to skip REST calls
					...(isEager && {
						isEager,
						parentTransferId: eagerParentEntityId,
						relationName: eagerRelationName,
					}),
				},
			}) as unknown as Parameters<typeof useActionDispatcher>[0],
		[
			navigation,
			dataStore,
			application,
			api,
			transfer,
			notifications,
			isEditMode,
			actionOverrides,
			isEager,
			eagerParentEntityId,
			eagerRelationName,
			refreshPage,
		]
	);

	// Initialize action dispatcher
	const { dispatch } = useActionDispatcher(dispatcherContext);

	// Wrap dispatch so that ALL RefreshActionDefinition dispatches automatically include
	// the view mask. This guarantees the _mask is sent regardless of call-site (useEffect,
	// button click, programmatic dispatch, downstream DispatchProvider consumers, etc.).
	const enrichedDispatch = useCallback(
		async (action: any) => {
			if (action.actionDefinition?.["@type"] === "RefreshActionDefinition" && viewMask) {
				return dispatch({
					...action,
					queryCustomizer: {
						...action.queryCustomizer,
						_mask: viewMask,
					},
				});
			}
			return dispatch(action);
		},
		[dispatch, viewMask]
	);

	// Use ref to track if onInit has been dispatched (avoid duplicate calls on strict mode re-renders)
	const hasDispatchedOnInit = useRef(false);
	// Use ref to store dispatch function to avoid it changing in effect deps
	const dispatchRef = useRef(enrichedDispatch);
	dispatchRef.current = enrichedDispatch;

	// Call onInit (Refresh) when view container mounts
	useEffect(() => {
		if (hasDispatchedOnInit.current) return;

		if (onInitAction && transfer) {
			hasDispatchedOnInit.current = true;

			// Dispatch the refresh action — enrichedDispatch automatically injects _mask
			dispatchRef.current(onInitAction).catch((error) => {
				console.error("[ViewContainerRenderer] Error dispatching onInit action:", error);
			});
		}
	}, [onInitAction, transfer]);

	// Re-dispatch onInit when refreshVersion changes (triggered by operations)
	useEffect(() => {
		if (refreshVersion === 0) return; // Skip initial mount
		if (onInitAction && transfer) {
			// enrichedDispatch automatically injects _mask
			dispatchRef.current(onInitAction).catch((error) => {
				console.error("[ViewContainerRenderer] Error refreshing page:", error);
			});
		}
	}, [refreshVersion, onInitAction, transfer]);

	const firstChild = container.children?.[0] as Flex | undefined;

	// Don't render PageHeader in dialogs - dialog handles its own title and actions
	const showPageHeader = !pageContext?.isDialog;

	return (
		<PageProvider
			transferId={effectiveEntityId}
			pageDefinition={page}
			params={effectiveParams}
			isDialog={pageContext?.isDialog ?? false}
		>
			<RefreshSignalProvider signal={refreshVersion}>
				<DispatchProvider dispatch={enrichedDispatch}>
					<Box
						data-testid={`view-container::${container.name ?? container["xmi:id"]}`}
						sx={{ animation: "judoFadeInUp 0.4s cubic-bezier(0.4,0,0.2,1) both" }}
					>
						{showPageHeader && <PageHeader page={page} editMode={isEditMode} onDispatch={enrichedDispatch} />}

						{/* View content - render children from visual model */}
						{/* Note: We do NOT force readOnly based on edit mode here.
						    Each input's readOnly state comes from its own isReadOnly attribute in the model.
						    Edit mode (isDirty) is used for showing Cancel/Save buttons, not for input editability. */}
						{firstChild && <FlexRenderer element={firstChild} />}
					</Box>
				</DispatchProvider>
			</RefreshSignalProvider>
		</PageProvider>
	);
}

export default ViewContainerRenderer;
